import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import {
  AuthForm,
  AuthField,
  AuthFormError,
  AuthFormFooter,
  AuthFormHeader,
  authInputClassName,
} from '../components/auth/AuthForm';
import { AuthOrDivider } from '../components/auth/AuthOrDivider';
import { GoogleAuthButton } from '../components/auth/GoogleAuthButton';
import { Button } from '../components/ui/Button';
import { Checkbox } from '../components/ui/Checkbox';
import { Input } from '../components/ui/Input';
import { PasswordInput } from '../components/ui/PasswordInput';
import pt from '../locales/pt';
import { useAuth } from '../store/AuthContext';

const schema = z
  .object({
    email: z
      .string()
      .min(1, pt.auth.errors.emailRequired)
      .email(pt.auth.errors.emailInvalid),
    password1: z.string().min(8, pt.auth.errors.passwordTooShort),
    password2: z.string(),
    acceptTerms: z.boolean(),
  })
  .refine((data) => data.password1 === data.password2, {
    path: ['password2'],
    message: pt.auth.errors.passwordsDontMatch,
  })
  .refine((data) => data.acceptTerms === true, {
    path: ['acceptTerms'],
    message: pt.auth.errors.termsRequired,
  });

function flattenServerError(error) {
  if (!error) return pt.auth.errors.generic;
  if (typeof error === 'string') return error;
  if (Array.isArray(error)) return error[0];
  for (const key of ['non_field_errors', 'detail', 'email', 'password1', 'password2']) {
    if (error[key]) {
      const value = error[key];
      return Array.isArray(value) ? value[0] : value;
    }
  }
  const firstKey = Object.keys(error)[0];
  if (firstKey) {
    const value = error[firstKey];
    return Array.isArray(value) ? value[0] : value;
  }
  return pt.auth.errors.generic;
}

export function SignUp() {
  const { register: registerUser } = useAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    control,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
      password1: '',
      password2: '',
      acceptTerms: false,
    },
  });

  const onSubmit = async (data) => {
    const response = await registerUser({
      email: data.email,
      password1: data.password1,
      password2: data.password2,
    });
    if (!response.success) {
      setError('root.server', { message: flattenServerError(response.error) });
      return;
    }
    navigate('/onboarding/welcome', { replace: true });
  };

  return (
    <>
      <AuthFormHeader
        title={pt.auth.signUp.title}
        subtitle={pt.auth.signUp.subtitle}
        chip={pt.auth.signUp.riskLine}
      />
      <AuthForm onSubmit={handleSubmit(onSubmit)}>
        <AuthFormError message={errors.root?.server?.message} />
        <GoogleAuthButton process="signup" className="w-full" size="lg" />
        <AuthOrDivider />
        <div className="flex flex-col gap-space-4 short:gap-space-1">
          <AuthField id="signup-email" label={pt.auth.fields.email} error={errors.email?.message}>
            <Input
              id="signup-email"
              type="email"
              autoComplete="email"
              invalid={!!errors.email}
              placeholder={pt.auth.fields.emailPlaceholder}
              className={authInputClassName}
              {...register('email')}
            />
          </AuthField>
          <AuthField
            id="signup-password1"
            label={pt.auth.fields.password}
            error={errors.password1?.message}
          >
            <PasswordInput
              id="signup-password1"
              autoComplete="new-password"
              invalid={!!errors.password1}
              placeholder={pt.auth.fields.passwordPlaceholder}
              className={authInputClassName}
              {...register('password1')}
            />
          </AuthField>
          <AuthField
            id="signup-password2"
            label={pt.auth.fields.passwordConfirm}
            error={errors.password2?.message}
          >
            <PasswordInput
              id="signup-password2"
              autoComplete="new-password"
              invalid={!!errors.password2}
              placeholder={pt.auth.fields.passwordConfirmPlaceholder}
              className={authInputClassName}
              {...register('password2')}
            />
          </AuthField>
        </div>

        <Controller
          control={control}
          name="acceptTerms"
          render={({ field }) => (
            <div className="flex flex-col gap-space-2">
              <label className="flex items-start gap-space-3 text-type-body-sm leading-relaxed text-foreground/80">
                <Checkbox
                  id="signup-terms"
                  checked={field.value}
                  invalid={!!errors.acceptTerms}
                  onCheckedChange={(checked) => field.onChange(checked === true)}
                  className="mt-0.5"
                />
                <span>
                  {pt.auth.signUp.termsPrefix}
                  <Link
                    to="/terms"
                    className="font-medium text-sage-700 hover:underline dark:text-sage-400"
                  >
                    {pt.auth.signUp.termsLinkTerms}
                  </Link>
                  {pt.auth.signUp.termsMiddle}
                  <Link
                    to="/privacy"
                    className="font-medium text-sage-700 hover:underline dark:text-sage-400"
                  >
                    {pt.auth.signUp.termsLinkPrivacy}
                  </Link>
                  {pt.auth.signUp.termsSuffix}
                </span>
              </label>
              {errors.acceptTerms?.message ? (
                <p className="text-type-body-sm text-danger-700">{errors.acceptTerms.message}</p>
              ) : null}
            </div>
          )}
        />

        <Button type="submit" size="lg" loading={isSubmitting} className="w-full">
          {isSubmitting ? pt.auth.signUp.submitting : pt.auth.signUp.submit}
        </Button>
      </AuthForm>
      <AuthFormFooter>
        <span>{pt.auth.signUp.haveAccount}</span>
        <Link
          to="/sign-in"
          className="font-semibold text-sage-700 hover:underline dark:text-sage-400"
        >
          {pt.auth.signUp.goSignIn}
        </Link>
      </AuthFormFooter>
    </>
  );
}
